import { useState } from 'react'
import { Card, Table, Tag, Typography, Button, Modal } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { auditsApi } from '@/api/audits'
import type { AuditLog } from '@/types/audit'
import type { ColumnsType } from 'antd/es/table'

const { Text } = Typography

interface UserAuditLogProps {
  userId: string
}

export const UserAuditLog = ({ userId }: UserAuditLogProps) => {
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [selectedAudit, setSelectedAudit] = useState<AuditLog | null>(null)

  // Fetch max 100 audits (single request, client-side pagination)
  const { data, isLoading } = useQuery({
    queryKey: ['user-audits', userId],
    queryFn: async () => {
      const response = await auditsApi.list({
        userId,
        page: 1,
        pageSize: 100,
      })
      return response.data
    },
  })

  const handleViewDetails = (audit: AuditLog) => {
    setSelectedAudit(audit)
    setDetailsModalOpen(true)
  }

  const getCategoryColor = (category: string | null) => {
    if (!category) return 'default'
    const categoryMap: Record<string, string> = {
      user: 'blue',
      permission: 'green',
      hospital: 'orange',
      auth: 'purple',
      system: 'cyan',
    }
    return categoryMap[category.toLowerCase()] || 'default'
  }

  const getActionColor = (action: string | null) => {
    if (!action) return 'default'
    const actionUpper = action.toUpperCase()
    if (actionUpper.includes('CREATE')) return 'success'
    if (actionUpper.includes('UPDATE') || actionUpper.includes('EDIT')) return 'processing'
    if (actionUpper.includes('DELETE') || actionUpper.includes('REMOVE')) return 'error'
    if (actionUpper.includes('VIEW') || actionUpper.includes('READ')) return 'default'
    return 'warning'
  }

  const columns: ColumnsType<AuditLog> = [
    {
      title: 'Timestamp',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (createdAt: string) => {
        const date = new Date(createdAt)
        return (
          <Text style={{ whiteSpace: 'nowrap' }}>
            {date.toLocaleDateString()} {date.toLocaleTimeString()}
          </Text>
        )
      },
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category: string | null) => (
        <Tag color={getCategoryColor(category)}>{category || 'Unknown'}</Tag>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 140,
      render: (action: string | null) => (
        <Tag color={getActionColor(action)}>{action || 'Unknown'}</Tag>
      ),
    },
    {
      title: 'Details',
      key: 'details',
      width: 120,
      render: (_: unknown, record: AuditLog) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)}>
          View
        </Button>
      ),
    },
    {
      title: 'IP Address',
      dataIndex: 'ip',
      key: 'ip',
      width: 130,
      render: (ip: string | null) => (
        <Text code style={{ whiteSpace: 'nowrap' }}>
          {ip || 'N/A'}
        </Text>
      ),
    },
  ]

  return (
    <>
      <Card title="Recent Activity (Last 100)" style={{ marginTop: 16 }}>
        <Table
          columns={columns}
          dataSource={data?.audits || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} entries`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title="Audit Details"
        open={detailsModalOpen}
        onCancel={() => {
          setDetailsModalOpen(false)
          setSelectedAudit(null)
        }}
        footer={null}
        width={800}
      >
        {selectedAudit && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Timestamp: </Text>
              <Text>{new Date(selectedAudit.createdAt).toLocaleString()}</Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Category: </Text>
              <Tag color={getCategoryColor(selectedAudit.category)}>
                {selectedAudit.category || 'Unknown'}
              </Tag>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Action: </Text>
              <Tag color={getActionColor(selectedAudit.action)}>
                {selectedAudit.action || 'Unknown'}
              </Tag>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>IP Address: </Text>
              <Text code>{selectedAudit.ip || 'N/A'}</Text>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Details:</Text>
            </div>
            <pre
              style={{
                background: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
                overflow: 'auto',
                maxHeight: '400px',
                fontSize: '12px',
                lineHeight: '1.5',
                margin: 0,
                border: '1px solid #d9d9d9',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(selectedAudit.details, null, 2)}
            </pre>
          </div>
        )}
      </Modal>
    </>
  )
}
